// src/pages/TherapistsPage.js
/* eslint-disable no-unused-vars */
import { useNavigate } from 'react-router-dom'
import React, { useState, useEffect } from 'react'
import {
  collection,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  where,
} from 'firebase/firestore'
import imageCompression from 'browser-image-compression';
import { httpsCallable } from 'firebase/functions'
import { db, storage, functions } from '../firebaseConfig'
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage'

export default function TherapistsPage() {
  const navigate = useNavigate()
  const [therapists, setTherapists] = useState([])
  const [filtered, setFiltered] = useState([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [perPage] = useState(10)

  // katalog layanan
  const [serviceCatalog, setServiceCatalog] = useState([])
  const [catalogLoading, setCatalogLoading] = useState(false)

  // form
  const [showForm, setShowForm] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [previewUrl, setPreviewUrl] = useState(null)
  const [editMode, setEditMode] = useState(false)
  const [editId, setEditId] = useState(null)
  const [cityInput, setCityInput] = useState('')

  const [newTherapist, setNewTherapist] = useState({
    name: '',
    id_number: '',
    nik_ktp: '', // 👈 Tambahan state KTP
    phone: '',
    email: '',
    gender: '',
    password: '', 
    status: true,
    requireDeviceBinding: true, // 👈 TAMBAHAN: Default Wajib Kunci HP
    favoriteCount: 0,
    photoUrl: '',
    rating: 0.0,
    cities: [],
    services: [],
    referralCodeInput: '', // input kode referral upline (opsional)
    seragam: false, // [ADD] field seragam
    target_area_override: '', // 👈 TAMBAHAN: Field Kunci Area Manual
  })
  const normalizeGender = (value) => {
    if (value === 'pria') return 'male'
    if (value === 'wanita') return 'female'
    return value
  }
  const denormalizeGender = (value) => {
    if (value === 'male') return 'pria'
    if (value === 'female') return 'wanita'
    return value
  }

  // 👈 TAMBAHAN: Helper untuk format jam terakhir dilihat
  const formatLastSeen = (timestamp) => {
    if (!timestamp) return '';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' });
    } catch (e) {
      return '';
    }
  };

  // panel detail
  const [selectedTherapist, setSelectedTherapist] = useState(null)
  const [detailOpen, setDetailOpen] = useState(false)

  // ===== LOAD DATA LAYANAN (catalog) =====
  useEffect(() => {
    const loadCatalog = async () => {
      try {
        setCatalogLoading(true)
        const snap = await getDocs(collection(db, 'therapists_services'))
        const items = snap.docs.map((d) => {
          const data = d.data()
          const variantsMap =
            data.variants && typeof data.variants === 'object'
              ? data.variants
              : {}
          return {
            id: d.id,
            name: data.name || d.id,
            category: data.category || 'Other',
            active: data.active !== false,
            variants: Object.keys(variantsMap).sort(),
          }
        })
        setServiceCatalog(items)
      } finally {
        setCatalogLoading(false)
      }
    }
    loadCatalog()
  }, [])

  // 👈 FUNGSI PEMBANTU: Untuk memproses data mentah Firestore
  const processTherapistData = (snap) => {
    return snap.docs.map((d) => {
      const t = { id: d.id, ...d.data() }
      t.rating = typeof t.rating === 'number' ? t.rating : 0.0
      t.cities = Array.isArray(t.cities) ? t.cities : []
      t.status = typeof t.status === 'boolean' ? t.status : String(t.status).toLowerCase() === 'aktif'
      t.favoriteCount = t.favoriteCount || 0
      if (!Array.isArray(t.services)) t.services = []
      t.seragam = !!t.seragam
      // Data lifecycle untuk indikator Buka App
      t.is_app_open = d.data().is_app_open || false
      t.last_app_opened = d.data().last_app_opened || null
      return t
    })
  }

  // 👈 FUNGSI FETCH MANUAL: Agar tombol Add/Edit/Delete tidak error
  const fetchTherapists = async () => {
    const snap = await getDocs(collection(db, 'therapists'))
    const data = processTherapistData(snap)
    setTherapists(data)
    setFiltered(data)
  }

  // 👈 LISTENER REAL-TIME: Otomatis update status Buka App
  useEffect(() => {
    setLoading(true)
    const unsub = onSnapshot(collection(db, 'therapists'), (snap) => {
      const data = processTherapistData(snap)
      setTherapists(data)
      setFiltered(data)
      setLoading(false)
    }, (err) => {
      console.error("Real-time error:", err)
      setLoading(false)
    })

    return () => unsub() 
  }, [])

  // ===== SEARCH =====
  useEffect(() => {
    const s = search.toLowerCase()
    const f = therapists.filter((t) => {
      const serviceFlat = Array.isArray(t.services)
        ? t.services
            .map((x) => x.serviceId + ':' + (x.variants || []).join('|'))
            .join(',')
        : ''
      return (
        (t.name || '').toLowerCase().includes(s) ||
        (t.email || '').toLowerCase().includes(s) ||
        (t.phone || '').toLowerCase().includes(s) ||
        (t.id_number || '').toLowerCase().includes(s) ||
        serviceFlat.toLowerCase().includes(s) ||
        (Array.isArray(t.cities) ? t.cities.join(',') : '')
          .toLowerCase()
          .includes(s)
      )
    })
    setFiltered(f)
    setPage(1)
  }, [search, therapists])

  const totalPages = Math.ceil(filtered.length / perPage)
  const currentData = filtered.slice((page - 1) * perPage, page * perPage)

  // ===== UPLOAD FOTO =====
  // ===== UPLOAD FOTO (DENGAN KOMPRESI) =====
  const handleUploadPhoto = async (file) => { // 👈 Tambahkan async
    if (!file) return alert('Pilih file terlebih dahulu!')
    if (!['image/webp', 'image/jpeg', 'image/png'].includes(file.type))
      return alert('File harus .webp, .jpg, atau .png')

    setUploading(true); // 👈 Tampilkan status loading sejak mulai kompresi
    setUploadProgress(0);

    try {
      // 1. Opsi Kompresi (Kualitas tetap bagus, ukuran turun drastis)
      const options = {
        maxSizeMB: 0.2, // Maksimal 200KB (Sangat cukup untuk foto profil)
        maxWidthOrHeight: 800, // Dimensi maksimal 800px agar tetap tajam
        useWebWorker: true,
        // fileType: 'image/webp' // (Opsional) bisa otomatis convert ke webp
      };

      // 2. Jalankan Kompresi
      const compressedFile = await imageCompression(file, options);
      
      // 3. Upload file yang sudah dikompresi
      const storageRef = ref(
        storage,
        `therapists_photos/${Date.now()}_${compressedFile.name}`
      )
      const uploadTask = uploadBytesResumable(storageRef, compressedFile)
      
      uploadTask.on(
        'state_changed',
        (snapshot) =>
          setUploadProgress(
            ((snapshot.bytesTransferred / snapshot.totalBytes) * 100).toFixed(0)
          ),
        (error) => {
          setUploading(false)
          alert('Gagal upload: ' + error.message)
        },
        async () => {
          const url = await getDownloadURL(uploadTask.snapshot.ref)
          setNewTherapist((prev) => ({ ...prev, photoUrl: url }))
          setPreviewUrl(url)
          setUploading(false)
          alert('✅ Foto berhasil dikompresi & diunggah!')
        }
      )
    } catch (error) {
      console.error("Error saat kompresi:", error);
      setUploading(false);
      alert('Gagal mengompres gambar.');
    }
  }

  // ===== KOTA =====
  const addCity = () => {
    const v = cityInput.trim()
    if (!v) return
    if (newTherapist.cities.includes(v)) return setCityInput('')
    setNewTherapist((p) => ({ ...p, cities: [...p.cities, v] }))
    setCityInput('')
  }

  const removeCityLocal = (city) => {
    setNewTherapist((p) => ({
      ...p,
      cities: p.cities.filter((c) => c !== city),
    }))
  }

  // ===== ADD / EDIT TERAPIS =====
  const handleAdd = async (e) => {
    e.preventDefault()
    // 👈 Tambahkan nik_ktp ke destrukturisasi
    const { name, phone, email, gender, password, photoUrl, nik_ktp } = newTherapist 
    
    if (!name || !phone || !email || !gender || !nik_ktp)
      return alert('Lengkapi data wajib (termasuk Nomor KTP)!')

    if (nik_ktp.length !== 16) 
      return alert('Nomor KTP harus tepat 16 digit!')
    
    if (!editMode && !password) 
      return alert('Password awal wajib diisi!')
      
    if (!photoUrl) return alert('Upload foto profil dahulu!')

    try {
      setLoading(true)

      // 👈 TAMBAHAN: CEK DUPLIKAT KTP
      const ktpQuery = query(collection(db, 'therapists'), where('nik_ktp', '==', nik_ktp.trim()));
      const ktpSnap = await getDocs(ktpQuery);
      let isDuplicate = false;
      
      ktpSnap.forEach((doc) => {
        // Abaikan KTP milik sendiri saat edit
        if (!editMode || doc.id !== editId) {
          isDuplicate = true;
        }
      });

      if (isDuplicate) {
        setLoading(false);
        return alert('❌ Gagal: Nomor KTP ini sudah terdaftar pada mitra lain!');
      }

      // payload dasar (dipakai baik edit maupun create)
      const payload = {
        ...newTherapist,
        nik_ktp: newTherapist.nik_ktp.trim(), // 👈 Tambahkan NIK ke payload
        phone: newTherapist.phone.trim(),
        email: newTherapist.email.trim(), 
        name: newTherapist.name.trim(),
        gender: normalizeGender(newTherapist.gender),
        rating: parseFloat(newTherapist.rating || 0),
        status: !!newTherapist.status,
        requireDeviceBinding: !!newTherapist.requireDeviceBinding,
        referralCodeInput: newTherapist.referralCodeInput?.trim() || null, 
        seragam: !!newTherapist.seragam, 
        target_area_override: newTherapist.target_area_override || null,
      } // 👈 PASTIKAN PAYLOAD DITUTUP DENGAN BENAR

      // HAPUS try { setLoading(true) } YANG KEDUA KARENA SUDAH ADA DI ATAS SEBELUM QUERY KTP

      if (editMode) {
        // ✏️ EDIT: langsung ke Firestore
        await updateDoc(doc(db, 'therapists', editId), payload)
        // ✏️ EDIT: langsung ke Firestore (tidak mengubah relasi referral)
        await updateDoc(doc(db, 'therapists', editId), payload)
        alert('✅ Data diperbarui')
      } else {
        // ➕ CREATE: lewat Cloud Function createTherapist
        const createTherapist = httpsCallable(functions, 'createTherapist')
        const res = await createTherapist(payload)
        const data = res.data || {}

        if (!data.success) {
          alert(
            `⚠️ Gagal menambahkan terapis: ${
              data.error || 'Terjadi kesalahan pada server.'
            }`
          )
          return
        }

        alert(
          `✅ Terapis berhasil dibuat.\nID: ${data.id_number || '—'}\nUID: ${
            data.uid || '—'
          }`
        )
      }

      // reset form setelah sukses
      setShowForm(false)
      setPreviewUrl(null)
      setEditMode(false)
      setNewTherapist({
        name: '',
        id_number: '',
        phone: '',
        email: '',
        gender: '',
        password: '', // 👈 PASTIKAN DIRESET KOSONG LAGI
        status: true,
        favoriteCount: 0,
        photoUrl: '',
        rating: 0.0,
        cities: [],
        services: [],
        referralCodeInput: '',
        seragam: false, // [ADD] reset seragam
        target_area_override: '', // 👈 TAMBAHAN: Kosongkan dropdown area
      })
      setCityInput('')
      fetchTherapists()
    } catch (error) {
      console.error('❌ Error:', error)
      alert('Gagal menyimpan data: ' + error.message)
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = (t) => {
    setEditMode(true)
    setEditId(t.id)
    setShowForm(true)
    setNewTherapist({
      name: t.name || '',
      id_number: t.id_number || '',
      nik_ktp: t.nik_ktp || '', // 👈 Tarik data KTP lama
      phone: t.phone || '',
      email: t.email || '',
      gender: denormalizeGender(t.gender || ''),
      status: t.status ?? true,
      favoriteCount: t.favoriteCount || 0,
      photoUrl: t.photoUrl || '',
      rating: t.rating || 0,
      cities: Array.isArray(t.cities) ? t.cities : [],
      services: Array.isArray(t.services) ? t.services : [],
      // saat edit, kita tidak pakai referralCodeInput lagi untuk ubah relasi referral
      referralCodeInput: '', // dikosongkan supaya tidak disalahgunakan
      seragam: !!t.seragam, // [ADD] set seragam from existing data
      requireDeviceBinding: t.requireDeviceBinding ?? true, // 👈 TAMBAHAN (Default true jika data lama)
      target_area_override: t.target_area_override || '', // 👈 TAMBAHAN: Tarik pengaturan area lama
    })
    setPreviewUrl(t.photoUrl || null)
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Yakin ingin menghapus terapis ini?')) return
    await deleteDoc(doc(db, 'therapists', id))
    fetchTherapists()
  }

  const toggleStatus = async (t) => {
    await updateDoc(doc(db, 'therapists', t.id), { status: !t.status })
    fetchTherapists()
  }

  const resetPassword = async (therapist) => {
    if (!therapist.uid) {
      alert('UID tidak ditemukan. Akun lama perlu dimigrasi.')
      return
    }

    const newPass = window.prompt(
      `Masukkan password baru untuk ${therapist.name}:`
    )

    if (!newPass || newPass.length < 6) {
      alert('Password minimal 6 karakter.')
      return
    }

    try {
      setLoading(true)
      const setMitraPassword = httpsCallable(functions, 'setMitraPassword')
      const res = await setMitraPassword({
        uid: therapist.uid,
        password: newPass,
      })

      if (res.data?.success) {
        alert('✅ Password berhasil di-reset.')
      } else {
        alert('❌ Gagal reset password.')
      }
    } catch (err) {
      console.error(err)
      alert('Terjadi kesalahan saat reset password.')
    } finally {
      setLoading(false)
    }
  }
  const resetDevice = async (therapist) => {
    if (!window.confirm(`Yakin ingin mereset Kunci HP untuk ${therapist.name}?\n\nSetelah direset, mereka bisa login di HP baru.`)) {
      return
    }

    try {
      setLoading(true)
      // Kosongkan field deviceId agar sistem bisa merekam HP baru
      await updateDoc(doc(db, 'therapists', therapist.id), { 
        deviceId: '' 
      })
      alert('✅ Kunci HP berhasil direset!')
      fetchTherapists()
    } catch (error) {
      console.error(error)
      alert('❌ Gagal mereset HP: ' + error.message)
    } finally {
      setLoading(false)
    }
  }

  const openTherapistDetail = (therapist) => {
    setSelectedTherapist(therapist)
    setDetailOpen(true)
  }

  // ===== RENDER =====
  return (
    <div className="min-h-screen bg-gray-950 text-gray-200 p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold">💆 Manajemen Terapis</h2>
        
        {/* 👈 BUNGKUS DENGAN DIV AGAR TOMBOL SEJAJAR */}
        <div className="flex items-center gap-2">
          
          

          <button
            onClick={() => {
              setShowForm(!showForm)
              setEditMode(false)
            setNewTherapist({
              name: '',
              id_number: '',
              phone: '',
              email: '',
              gender: '',
              password: '', // 👈 PASTIKAN DIRESET KOSONG LAGI
              status: true,
              favoriteCount: 0,
              photoUrl: '',
              rating: 0.0,
              cities: [],
              services: [],
              referralCodeInput: '',
              seragam: false, // [ADD] reset seragam
              target_area_override: '', // 👈 TAMBAHAN: Kosongkan dropdown area
            })
            setPreviewUrl(null)
            setCityInput('')
          }}
          className={`${
            showForm
              ? 'bg-red-600 hover:bg-red-700'
              : 'bg-blue-600 hover:bg-blue-700'
          } px-4 py-2 rounded-lg text-white`}
        >
          {showForm ? '❌ Batal Tambah' : '➕ Tambah Terapis'}
        </button>
      </div>
    </div>

      {/* FORM */}
      {showForm && (
        <form
          onSubmit={handleAdd}
          className="bg-gray-900 p-4 mb-6 rounded-lg border border-gray-800"
        >
          <h3 className="font-semibold mb-3">
            {editMode ? '✏️ Edit Terapis' : '➕ Terapis Baru'}
          </h3>

          {/* INPUT GRID */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-4">
            <input
              type="text"
              placeholder="Nama"
              value={newTherapist.name}
              onChange={(e) =>
                setNewTherapist({ ...newTherapist, name: e.target.value })
              }
              className="p-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
            />
            {/* 👈 TAMBAHAN: Input KTP */}
            <input
              type="number"
              placeholder="Nomor KTP (16 Digit)"
              value={newTherapist.nik_ktp}
              onChange={(e) =>
                setNewTherapist({ ...newTherapist, nik_ktp: e.target.value })
              }
              className="p-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
            />
            <input
              type="email"
              placeholder="Email"
              value={newTherapist.email}
              onChange={(e) =>
                setNewTherapist({ ...newTherapist, email: e.target.value })
              }
              className="p-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
            />
            <input
              type="text"
              placeholder="Telepon"
              value={newTherapist.phone}
              onChange={(e) =>
                setNewTherapist({ ...newTherapist, phone: e.target.value })
              }
              className="p-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
            />

            {/* 👈 TAMBAHAN: Input Password (Hanya muncul saat Tambah Baru) */}
            {!editMode && (
              <input
                type="password"
                placeholder="Password Awal"
                value={newTherapist.password}
                onChange={(e) =>
                  setNewTherapist({ ...newTherapist, password: e.target.value })
                }
                className="p-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
              />
            )}

            <select
              value={newTherapist.gender}
              onChange={(e) =>
                setNewTherapist({ ...newTherapist, gender: e.target.value })
              }
              className="p-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
            >
              <option value="">Pilih Gender</option>
              <option value="pria">Pria</option>
              <option value="wanita">Wanita</option>
            </select>

            {/* Input Referral */}
            <input
              type="text"
              placeholder="Kode Referral (opsional)"
              value={newTherapist.referralCodeInput}
              onChange={(e) =>
                setNewTherapist({
                  ...newTherapist,
                  referralCodeInput: e.target.value,
                })
              }
              className="p-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
            />
          </div>

          {/* UPLOAD FOTO */}
          <div className="mb-4">
            <label className="block text-gray-300 mb-2">
              Foto Profil (.webp, .jpg, .png)
            </label>
            <input
              type="file"
              accept="image/webp, image/jpeg, image/png"
              onChange={(e) => handleUploadPhoto(e.target.files[0])}
              className="text-sm text-gray-400"
            />
            {uploading && (
              <div className="text-gray-400 text-sm mt-1">
                Mengunggah... {uploadProgress}%
              </div>
            )}
            {previewUrl && (
              <img
                src={previewUrl}
                alt="Preview"
                className="w-24 h-24 rounded-lg mt-3 border border-gray-700 object-cover"
              />
            )}
          </div>

          {/* Kota */}
          <div className="mb-4">
            <label className="block text-gray-300 mb-2">Kota (Cities)</label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                placeholder="Tambah kota lalu Enter / klik Tambah"
                value={cityInput}
                onChange={(e) => setCityInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    addCity()
                  }
                }}
                className="flex-1 p-2 bg-gray-800 border border-gray-700 rounded-lg text-white"
              />
              <button
                type="button"
                onClick={addCity}
                className="px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white"
              >
                Tambah
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {newTherapist.cities.map((c) => (
                <span
                  key={c}
                  className="inline-flex items-center bg-gray-800 border border-gray-700 px-2 py-1 rounded text-xs"
                >
                  {c}
                  <button
                    type="button"
                    onClick={() => removeCityLocal(c)}
                    className="ml-2 text-red-400 hover:text-red-500"
                  >
                    ✕
                  </button>
                </span>
              ))}
              {newTherapist.cities.length === 0 && (
                <span className="text-gray-500 text-sm italic">
                  Belum ada kota dipilih
                </span>
              )}
            </div>
          </div>

          {/* 👈 TAMBAHAN: Dropdown Kunci Area Target (Override) */}
          <div className="mb-4 bg-gray-800 p-3 rounded-lg border border-gray-700 w-full md:w-1/2">
            <label className="block text-gray-300 mb-2 font-semibold">Area Target Performa (Opsional)</label>
            <select
              value={newTherapist.target_area_override}
              onChange={(e) => setNewTherapist({ ...newTherapist, target_area_override: e.target.value })}
              className="w-full p-2 bg-gray-900 border border-gray-600 rounded-lg text-white outline-none focus:border-blue-500 uppercase"
            >
              <option value="">[ OTOMATIS BERDASARKAN KOTA/CITIES ]</option>
              <option value="jabodetabek">JABODETABEK</option>
              <option value="bandung">BANDUNG</option>
              <option value="semarang">SEMARANG</option>
              <option value="yogyakarta">YOGYAKARTA</option>
              <option value="surabaya">SURABAYA</option>
              <option value="denpasar">DENPASAR</option>
              <option value="makassar">MAKASSAR</option>
              <option value="manado">MANADO</option>
              <option value="balikpapan">BALIKPAPAN</option>
              <option value="batam">BATAM</option>
              <option value="palembang">PALEMBANG</option>
              <option value="pekanbaru">PEKANBARU</option>
              <option value="medan">MEDAN</option>
            </select>
            <p className="text-[10px] text-gray-400 mt-2">
              *Pilih area jika ingin mengunci target performa. Biarkan <b>[Otomatis]</b> agar sistem membaca lokasi kota dari field (Cities) mitra di atas.
            </p>
          </div>

          {/* Status, Seragam & Kunci HP */}
          <div className="mb-4 flex flex-wrap gap-6">
            
            {/* 1. Status Akun */}
            <div className="flex items-center gap-3">
              <label className="text-gray-300">Status Akun</label>
              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!newTherapist.status}
                  onChange={() =>
                    setNewTherapist((prev) => ({
                      ...prev,
                      status: !prev.status,
                    }))
                  }
                  className="form-checkbox w-5 h-5 text-blue-600 rounded focus:ring-blue-500 bg-gray-800 border-gray-600"
                />
                <span className="ml-2">
                  {newTherapist.status ? 'Aktif' : 'Nonaktif'}
                </span>
              </label>
            </div>

            {/* 2. Seragam */}
            <div className="flex items-center gap-3">
              <label className="text-gray-300">Seragam</label>
              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!newTherapist.seragam}
                  onChange={() =>
                    setNewTherapist((prev) => ({
                      ...prev,
                      seragam: !prev.seragam,
                    }))
                  }
                  className="form-checkbox w-5 h-5 text-blue-600 rounded focus:ring-blue-500 bg-gray-800 border-gray-600"
                />
                <span className="ml-2">
                  {newTherapist.seragam ? 'Punya' : 'Tidak'}
                </span>
              </label>
            </div>

            {/* 3. Kunci HP */}
            <div className="flex items-center gap-3">
              <label className="text-gray-300">Kunci HP</label>
              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!newTherapist.requireDeviceBinding}
                  onChange={() =>
                    setNewTherapist((prev) => ({
                      ...prev,
                      requireDeviceBinding: !prev.requireDeviceBinding,
                    }))
                  }
                  className="form-checkbox w-5 h-5 text-blue-600 rounded focus:ring-blue-500 bg-gray-800 border-gray-600"
                />
                <span className="ml-2">
                  {newTherapist.requireDeviceBinding ? 'Wajib (Terkunci)' : 'Bebas'}
                </span>
              </label>
            </div>
          </div>
          <button
            type="submit"
            disabled={loading}
            className="mt-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg disabled:opacity-50"
          >
            {loading
              ? editMode
                ? 'Menyimpan Perubahan...'
                : 'Menyimpan...'
              : editMode
              ? 'Simpan Perubahan'
              : 'Simpan Terapis'}
          </button>
        </form>
      )}

      {/* Search */}
      <input
        type="text"
        placeholder="Cari nama/email/telepon/ID..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="w-full p-2 mb-3 bg-gray-900 border border-gray-800 rounded-lg text-white"
      />

      {loading ? (
        <p className="text-gray-400">Memuat data...</p>
      ) : (
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
          {currentData.map((t) => (
            <div
              key={t.id}
              className="border-b border-gray-800 pb-3 mb-3 last:border-none"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <img
                    src={t.photoUrl}
                    alt={t.name}
                    className="w-12 h-12 rounded-full border border-gray-700 object-cover"
                  />
                  <div>
                    <p className="font-semibold text-white flex items-center flex-wrap gap-2">
                      {t.name}
                      <span className="text-xs text-gray-400">
                        ({t.id_number})
                      </span>
                      <span
                        className={`text-xs px-2 py-0.5 rounded ${
                          t.status
                            ? 'bg-green-900/30 text-green-400'
                            : 'bg-red-900/30 text-red-400'
                        }`}
                      >
                        {t.status ? 'Aktif' : 'Nonaktif'}
                      </span>

                      {/* 👈 TAMBAHAN: Indikator Buka Aplikasi */}
                      {t.is_app_open ? (
                        <span className="text-[10px] px-2 py-0.5 rounded bg-blue-900/40 text-blue-300 flex items-center gap-1.5 border border-blue-800">
                          <div className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse"></div>
                          Sedang Buka App
                        </span>
                      ) : (
                        <span className="text-[10px] px-2 py-0.5 rounded bg-gray-800 text-gray-400 flex items-center gap-1.5 border border-gray-700">
                          <div className="w-1.5 h-1.5 rounded-full bg-gray-500"></div>
                          Tutup App {t.last_app_opened ? `(${formatLastSeen(t.last_app_opened)})` : ''}
                        </span>
                      )}
                    </p>
                    <p className="text-gray-400 text-sm">
                      ⭐ {t.rating?.toFixed(1)} | {t.cities.join(', ')}
                    </p>
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => resetPassword(t)}
                    className="bg-orange-600 hover:bg-orange-700 text-white px-3 py-1 rounded-lg text-sm"
                  >
                    Reset Password
                  </button>

                  {/* 👈 TAMBAHAN: Tombol Reset HP */}
                  <button
                    onClick={() => resetDevice(t)}
                    className="bg-teal-600 hover:bg-teal-700 text-white px-3 py-1 rounded-lg text-sm"
                  >
                    📱 Reset HP
                  </button>

                  <button
                    onClick={() =>
                      navigate(
                        `/therapists/referrals?id=${t.id}&idNumber=${t.id_number}`
                      )
                    }
                    className="bg-purple-600 hover:bg-purple-700 text-white px-3 py-1 rounded-lg text-sm"
                  >
                    Referral
                  </button>

                  {/* DETAIL */}
                  <button
                    onClick={() => openTherapistDetail(t)}
                    className="bg-gray-700 hover:bg-gray-600 text-white px-3 py-1 rounded-lg text-sm"
                  >
                    Detail
                  </button>

                  {/* EDIT */}
                  <button
                    onClick={() => handleEdit(t)}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-lg text-sm"
                  >
                    Edit
                  </button>

                  {/* EDIT LAYANAN */}
                  <button
                    onClick={() =>
                      navigate(`/therapists/manage-services?id=${t.id}`)
                    }
                    className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1 rounded-lg text-sm"
                  >
                    Edit Layanan
                  </button>

                  {/* RATING */}
                  <button
                    onClick={() => navigate(`/therapists/rating?id=${t.id}`)}
                    className="bg-yellow-600 hover:bg-yellow-700 text-white px-3 py-1 rounded-lg text-sm"
                  >
                    Rating
                  </button>

                  {/* STATUS */}
                  <button
                    onClick={() => toggleStatus(t)}
                    className={`${
                      t.status
                        ? 'bg-yellow-600 hover:bg-yellow-700'
                        : 'bg-green-600 hover:bg-green-700'
                    } text-white px-3 py-1 rounded-lg text-sm`}
                  >
                    {t.status ? 'Nonaktifkan' : 'Aktifkan'}
                  </button>

                  {/* HAPUS */}
                  <button
                    onClick={() => handleDelete(t.id)}
                    className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded-lg text-sm"
                  >
                    Hapus
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {/* ========================================================== */}
      {/* 🟢 MULAI KODE BARU: COPY DARI SINI SAMPAI TANDA MERAH 🟢 */}
      {/* ========================================================== */}

      {!loading && filtered.length > perPage && (
        <div className="flex justify-between items-center mt-6 px-2">
          {/* Tombol Sebelumnya */}
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
              page === 1
                ? 'bg-gray-800 text-gray-600 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg'
            }`}
          >
            ⬅ Sebelumnya
          </button>

          {/* Info Halaman */}
          <div className="text-gray-400 text-sm bg-gray-900 px-4 py-2 rounded-full border border-gray-800">
            Halaman <span className="text-white font-bold mx-1">{page}</span> 
            dari <span className="text-white font-bold mx-1">{totalPages}</span>
          </div>

          {/* Tombol Selanjutnya */}
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
              page === totalPages
                ? 'bg-gray-800 text-gray-600 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg'
            }`}
          >
            Selanjutnya ➡
          </button>
        </div>
      )}

      {/* ========================================================== */}
      {/* 🔴 AKHIR KODE BARU 🔴 */}
      {/* ========================================================== */}

      {/* DETAIL PANEL */}
      {detailOpen && selectedTherapist && (
        <div className="fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => setDetailOpen(false)}
          />
          <div className="absolute right-0 top-0 h-full w-full max-w-md bg-gray-900 border-l border-gray-800 shadow-xl flex flex-col">
            <div className="p-4 border-b border-gray-800 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-blue-400">
                Detail Terapis
              </h3>
              <button
                onClick={() => setDetailOpen(false)}
                className="text-gray-400 hover:text-white"
              >
                ✕
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 text-gray-300">
              <div className="flex flex-col items-center text-center mb-5">
                <img
                  src={selectedTherapist.photoUrl || '/default-profile.webp'}
                  alt={selectedTherapist.name}
                  className="w-24 h-24 rounded-full border-2 border-blue-500 object-cover mb-3"
                />
                <h3 className="text-xl font-semibold text-white">
                  {selectedTherapist.name}
                </h3>
                <p className="text-gray-400 text-sm">
                  ID: {selectedTherapist.id_number}
                </p>
                <div className="mt-1 text-yellow-400 font-medium">
                  ⭐ {Number(selectedTherapist.rating || 0).toFixed(1)}
                </div>
              </div>

              <div className="space-y-2 text-sm">
                <p>
                  <b>Email:</b> {selectedTherapist.email}
                </p>
                <p>
                  <b>Telepon:</b> {selectedTherapist.phone}
                </p>
                <p>
                  <b>Gender:</b> {selectedTherapist.gender}
                </p>
                <p>
                  <b>Kota:</b> {selectedTherapist.cities?.join(', ') || '-'}
                </p>
                <p>
                  <b>Status:</b>{' '}
                  {selectedTherapist.status ? 'Aktif ✅' : 'Nonaktif ❌'}
                </p>
                {/* [ADD] Show Seragam in Detail */}
                <p>
                  <b>Seragam:</b>{' '}
                  {selectedTherapist.seragam ? (
                    <span className="text-green-400 font-bold">Punya</span>
                  ) : (
                    <span className="text-gray-500">Tidak</span>
                  )}
                </p>
              </div>

              <div className="mt-4">
                <p className="font-semibold text-white mb-1">Layanan</p>
                {selectedTherapist.services?.length > 0 ? (
                  <ul className="list-disc list-inside text-gray-400 text-sm">
                    {selectedTherapist.services.map((svc, i) => (
                      <li key={i}>
                        {svc.serviceId}{' '}
                        {svc.variants?.length > 0 && (
                          <span className="text-gray-500">
                            ({svc.variants.join(', ')})
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-gray-500 italic">Belum ada layanan</p>
                )}
              </div>

              <div className="mt-6">
                <button
                  onClick={() =>
                    (window.location.href = `/ratings?mitraId=${selectedTherapist.id_number}&mitraType=therapist`)
                  }
                  className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-md text-white text-sm font-medium"
                >
                  ⭐ Lihat Semua Ulasan Mitra Ini
                </button>
              </div>
            </div>

            <div className="p-4 border-t border-gray-800 flex justify-end">
              <button
                onClick={() => setDetailOpen(false)}
                className="px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-white text-sm"
              >
                Tutup
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}